import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Star, KeyRound } from 'lucide-react';
import { getBackendUrl } from '../../services/origin';
import { BrandLogo } from './BrandLogo';
import { AddToHomeHint } from './AddToHomeHint';

const API = getBackendUrl();

/* ── VERIFY SCREEN (dynamic brand colors) ───────────────────── */
export function VerifyScreen({ token, pin, setPin, verifying, onVerify, brand, businessName }) {
  const pc = brand.primary_color;
  const bg = brand.background_color;
  const tc = brand.text_color || '#FFFFFF';
  const ts = brand.text_secondary_color || '#888888';
  const hiddenInputRef = useRef(null);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverPhone, setRecoverPhone] = useState('');
  const [recovering, setRecovering] = useState(false);

  const focusInput = () => {
    if (hiddenInputRef.current) hiddenInputRef.current.focus();
  };

  const handleRecover = async () => {
    if (!recoverPhone.trim() || recoverPhone.trim().length < 7) {
      toast.error('Inserisci il numero di telefono'); return;
    }
    setRecovering(true);
    try {
      await axios.post(`${API}/api/customer/forgot-pin`, { phone: recoverPhone.trim() });
      toast.success('Se il numero è registrato riceverai un SMS con il PIN.');
      setRecoverOpen(false);
      setRecoverPhone('');
    } catch (err) {
      // Always show success-like message (don't leak which numbers exist)
      toast.success('Se il numero è registrato riceverai un SMS con il PIN.');
      setRecoverOpen(false);
      setRecoverPhone('');
    } finally { setRecovering(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bg }}>
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />
      <div className="w-full max-w-sm animate-fade-in-up" data-testid="verify-screen">
        <div className="text-center mb-8">
          {brand.logo_url ? (
            <BrandLogo src={brand.logo_url} name={businessName} size={56} primaryColor={pc} className="mx-auto mb-3" testid="verify-brand-logo" />
          ) : (
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${pc}20` }}>
              <Star size={28} style={{ color: pc }} />
            </div>
          )}
          {businessName && <p className="text-xs font-semibold tracking-wide uppercase mb-1" style={{ color: `${pc}cc` }}>{businessName}</p>}
          <h1 className="text-xl font-bold" style={{ color: tc }}>La tua bacheca</h1>
          <p className="text-sm mt-2" style={{ color: ts }}>Inserisci il PIN a 4 cifre ricevuto via SMS</p>
        </div>
        <div className="rounded-2xl p-6 border" style={{ backgroundColor: `${tc}08`, borderColor: `${tc}0a` }}>
          {/* Hidden numeric input — drives the visible slots. Tapping a slot
              focuses this input which opens the device numeric keyboard. */}
          <input
            ref={hiddenInputRef}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={4}
            value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            data-testid="verify-input"
            aria-label="PIN a 4 cifre"
            className="absolute opacity-0 pointer-events-none"
            style={{ width: 0, height: 0 }}
            onKeyDown={e => e.key === 'Enter' && onVerify()}
            autoFocus
          />
          <button
            type="button"
            onClick={focusInput}
            data-testid="pin-slots"
            className="w-full flex justify-center gap-3 mb-6 cursor-text"
            aria-label="Tocca per inserire il PIN"
          >
            {[0, 1, 2, 3].map(i => (
              <div key={i} data-testid={`pin-slot-${i}`}
                className="w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-all"
                style={{
                  borderColor: pin[i] ? pc : `${tc}25`,
                  backgroundColor: pin[i] ? `${pc}12` : `${tc}05`,
                  color: pin[i] ? tc : `${ts}66`,
                }}>
                {pin[i] ? '•' : ''}
              </div>
            ))}
          </button>
          <button
            onClick={onVerify}
            disabled={verifying || pin.length !== 4}
            data-testid="verify-btn"
            className="w-full h-12 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-all active:scale-[0.98]"
            style={{ backgroundColor: pc }}
          >
            {verifying ? 'Verifica...' : 'Accedi'}
          </button>
          <button
            type="button"
            onClick={() => setRecoverOpen(o => !o)}
            data-testid="forgot-pin-btn"
            className="w-full mt-3 h-10 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
            style={{ color: ts }}
          >
            <KeyRound size={13} /> Ho dimenticato il PIN
          </button>

          {recoverOpen && (
            <div className="mt-3 rounded-xl border p-3 animate-fade-in-up" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}10` }} data-testid="forgot-pin-form">
              <p className="text-xs mb-2" style={{ color: ts }}>Inserisci il tuo numero di telefono. Ti rinvieremo il PIN via SMS.</p>
              <input
                type="tel"
                inputMode="tel"
                value={recoverPhone}
                onChange={e => setRecoverPhone(e.target.value)}
                placeholder="es. 333 1234567"
                data-testid="forgot-pin-phone"
                className="w-full h-10 border rounded-xl px-3 text-sm focus:outline-none mb-2"
                style={{ backgroundColor: bg, borderColor: `${tc}20`, color: tc }}
              />
              <button
                onClick={handleRecover}
                disabled={recovering}
                data-testid="forgot-pin-submit"
                className="w-full h-10 rounded-xl text-white text-xs font-semibold disabled:opacity-40"
                style={{ backgroundColor: pc }}
              >
                {recovering ? 'Invio in corso…' : 'Invia PIN via SMS'}
              </button>
            </div>
          )}
        </div>

        {/* Add to home screen hint */}
        <AddToHomeHint brand={brand} />
      </div>
    </div>
  );
}
